import { List, OrderedMap } from 'immutable';

import { LIST_HEADER_ITEM_HEIGHT, LIST_ITEM_HEIGHT } from 'flow/flow-theme';
import { ContactListKey } from 'flow/selectors/contact-list-selectors';
import {
    ContactKey,
    ExtensionKey,
    ThirdPartyContactKey,
} from 'flow/types/EntityKeys';
import {
    isContactKey,
    isEmptyNullOrUndefined,
    isExtensionKey,
    isThirdPartyContactKey,
} from 'flow/utils';

/** The item key of the central switch Overview, as its page opens it. */
export const CENTRAL_SWITCH_OVERVIEW_KEY = 'central-switch-overview';

export type ContactListRow =
    | { kind: 'centralSwitchOverview' }
    | {
          kind: 'sectionTitle';
          /** Also the key the fold state is stored under. */
          sectionId: string;
          title: string;
          isOpen: boolean;
          canToggle: boolean;
      }
    | {
          kind: 'extension' | 'externalContact' | 'thirdPartyContact';
          entityKey: ContactListKey;
          /** The contact's copy in Favourites, a row of its own. */
          isFavorite?: boolean;
      }
    | { kind: 'connectedServicesInstructions' }
    | { kind: 'unknownKey'; key: string };

export interface ContactListRowsInput {
    searchText: string;
    showOverviewRow: boolean;
    ownExtensionKey: ExtensionKey;
    extensionKeysByDepartment: OrderedMap<string, List<ExtensionKey>>;
    externalContactKeys: List<ContactKey> | null;
    thirdPartyContactKeys: List<ThirdPartyContactKey> | null;
    favoriteKeys: List<ContactListKey>;
    searchResultKeys: List<ContactListKey>;
    connectedServicesAreOffered: boolean;
    hasSyncedSocialIntegrationAccount: boolean;
    sectionIsOpen: (sectionId: string) => boolean;
    translate: (key: string) => string;
}

export const contactListRowHeight = (row: ContactListRow) =>
    row.kind === 'sectionTitle' ? LIST_HEADER_ITEM_HEIGHT : LIST_ITEM_HEIGHT;

const entityRow = (
    input: ContactListRowsInput,
    key: ContactListKey,
): ContactListRow => {
    if (isExtensionKey(key) && key !== input.ownExtensionKey) {
        return { kind: 'extension', entityKey: key };
    }
    if (isContactKey(key)) {
        return { kind: 'externalContact', entityKey: key };
    }
    if (isThirdPartyContactKey(key)) {
        return { kind: 'thirdPartyContact', entityKey: key };
    }
    return { kind: 'unknownKey', key };
};

const section = (
    input: ContactListRowsInput,
    title: string,
    rows: ContactListRow[],
    { sectionId = title, canToggle = true } = {},
): ContactListRow[] => {
    const isOpen = canToggle ? input.sectionIsOpen(sectionId) : true;

    return [
        { kind: 'sectionTitle', sectionId, title, isOpen, canToggle },
        ...(isOpen ? rows : []),
    ];
};

const keyRows = (input: ContactListRowsInput, keys: List<ContactListKey>) =>
    keys.toArray().map((key) => entityRow(input, key));

const departmentRows = (
    input: ContactListRowsInput,
    department: string,
    extensionKeys: List<ExtensionKey>,
) => {
    // An empty name falls back to "No department"; an undefined one does not.
    const title =
        department || department !== ''
            ? department
            : input.translate('extensionlist.nodepartment');
    const otherColleagues = extensionKeys
        .filter((extensionKey) => extensionKey !== input.ownExtensionKey)
        .toList();

    return section(input, title, keyRows(input, otherColleagues));
};

const FAVORITES_SECTION_ID = 'favorites';

const asFavorite = (row: ContactListRow): ContactListRow =>
    'entityKey' in row ? { ...row, isFavorite: true } : row;

const favoriteRows = (input: ContactListRowsInput) =>
    input.favoriteKeys.isEmpty()
        ? []
        : section(
              input,
              input.translate('General.Favorites'),
              keyRows(input, input.favoriteKeys).map(asFavorite),
              { sectionId: FAVORITES_SECTION_ID },
          );

const colleagueRows = (input: ContactListRowsInput) => {
    const { extensionKeysByDepartment } = input;
    const defaultGroup = extensionKeysByDepartment.get('');

    if (extensionKeysByDepartment.size <= 1 && defaultGroup) {
        return departmentRows(
            input,
            input.translate('colleagues'),
            defaultGroup,
        );
    }

    return extensionKeysByDepartment
        .entrySeq()
        .toArray()
        .flatMap(([department, extensionKeys]) =>
            departmentRows(input, department, extensionKeys),
        );
};

const externalContactRows = (input: ContactListRowsInput) =>
    !input.externalContactKeys || input.externalContactKeys.isEmpty()
        ? []
        : section(
              input,
              input.translate('contacts.external'),
              keyRows(input, input.externalContactKeys),
          );

const thirdPartyContactRows = (input: ContactListRowsInput) => {
    if (!input.connectedServicesAreOffered) {
        return [];
    }

    if (!input.hasSyncedSocialIntegrationAccount) {
        return section(
            input,
            input.translate('extensionlist.other'),
            [{ kind: 'connectedServicesInstructions' }],
            { canToggle: false },
        );
    }

    if (!input.thirdPartyContactKeys || input.thirdPartyContactKeys.isEmpty()) {
        return [];
    }

    return section(
        input,
        input.translate('extensionlist.other'),
        keyRows(input, input.thirdPartyContactKeys),
        { sectionId: 'extensionlist.other' },
    );
};

const searchResultRows = (input: ContactListRowsInput) =>
    input.searchResultKeys.isEmpty()
        ? []
        : section(
              input,
              input.translate('search.results'),
              keyRows(input, input.searchResultKeys),
          );

export const buildContactListRows = (
    input: ContactListRowsInput,
): ContactListRow[] => {
    if (!isEmptyNullOrUndefined(input.searchText)) {
        return searchResultRows(input);
    }

    return [
        ...(input.showOverviewRow
            ? [{ kind: 'centralSwitchOverview' } as const]
            : []),
        ...favoriteRows(input),
        ...colleagueRows(input),
        ...externalContactRows(input),
        ...thirdPartyContactRows(input),
    ];
};

// Names a row for the list: for react-window, the highlight and the Tab stop.
// A favourite is a row of its own, apart from the contact's copy in its
// department. So when the favourite is removed or Favourites is folded, the
// highlight goes with its row, and focus goes to the nearest row.
export const contactListRowKey = (row: ContactListRow) => {
    switch (row.kind) {
        case 'centralSwitchOverview':
            return row.kind;
        case 'sectionTitle':
            return `section:${row.sectionId}`;
        case 'extension':
        case 'externalContact':
        case 'thirdPartyContact':
            return row.isFavorite
                ? `${FAVORITES_SECTION_ID}:${row.entityKey}`
                : row.entityKey;
        case 'unknownKey':
            return row.key;
        default:
            return null;
    }
};

export const rowIsFocusable = (row: ContactListRow) => {
    switch (row.kind) {
        case 'centralSwitchOverview':
        case 'extension':
        case 'externalContact':
        case 'thirdPartyContact':
            return true;
        case 'sectionTitle':
            return row.canToggle;
        default:
            return false;
    }
};
